import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { ScrollIntoView, Alert, Link, Button, RichTextArea } from '@grandvisionhq/common';
import { Heading, Divider } from '@grandvisionhq/elements';
import { Form, Email, Password } from '@grandvisionhq/forms';
import { useLink } from '@grandvisionhq/frk-routing';
import { connect, useIntl, statusFailed, statusLoading } from '@grandvisionhq/state';
import { useTrackPageView } from '@grandvisionhq/svc-tealium/events';
import { useTealium } from '@grandvisionhq/svc-tealium/provider';
import { SocialLogin } from '../components/SocialLogin/SocialLogin.js';
import { useAdditionalLoginProviders } from '../hooks/use-additional-login-providers.js';
import { useSocialLoginFlow } from '../hooks/use-social-login-flow.js';
import { resetPassword } from '../ResetPassword/actions.js';
import { getAuth, getFormattedRoutes } from '../selectors.js';
import { loginWithPassword } from './actions.js';

const LABEL_PREFIX = 'account.login';
const ERROR_PREFIX = `${LABEL_PREFIX}.errors`;
var Auth0ErrorCode;
(function(Auth0ErrorCode) {
    Auth0ErrorCode["AccessDenied"] = 'access_denied';
    Auth0ErrorCode["PasswordLeaked"] = 'password_leaked';
})(Auth0ErrorCode || (Auth0ErrorCode = {}));
var UrlStateParamError;
(function(UrlStateParamError) {
    UrlStateParamError["RequiresPasswordChange"] = 'requiresPasswordChange';
})(UrlStateParamError || (UrlStateParamError = {}));
const NonCompliantPasswordMessage = (props)=>{
    const { href: resetPasswordRoute  } = useLink({
        route: 'auth.reset-password'
    }) || {};
    const { getLabel  } = useIntl();
    const label = getLabel(`${ERROR_PREFIX}.requiresPasswordChange`);
    const handleClick = React.useCallback(()=>{
        props.resetPassword({
            email: props.email || ''
        });
    }, [
        props.resetPassword,
        props.email
    ]);
    /*
   * Look for the [ ] and take the part before, inside and after to create a text with
   * the link inside, like: "before" + <a>"inside"<a/> + "after"
   */ const regex = /(.+)\[(.+)\](.+)/g;
    const linkElement = props.email ? /*#__PURE__*/ React.createElement("button", {
        className: "login__reset-link",
        onClick: handleClick,
        "data-t": "non-compliant-password-action"
    }, label.replace(regex, '$2')) : /*#__PURE__*/ React.createElement("a", {
        className: "login__reset-link",
        href: resetPasswordRoute || '#',
        "data-t": "non-compliant-password-action"
    }, label.replace(regex, '$2'));
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", null, label.replace(regex, '$1')), linkElement, /*#__PURE__*/ React.createElement("span", null, label.replace(regex, '$3')));
};
NonCompliantPasswordMessage.displayName = 'NonCompliantPasswordMessage';
const initialFieldState = {
    isTouched: false,
    isValid: false,
    value: ''
};
const Login = ({ auth , email , error , className , loginWithPassword: loginWithPassword1 , createAccountRichText , disableSignup , showSocialLogin =true , returnPath , resetPassword: resetPassword1 , showTitle =true  })=>{
    const { hasProviders  } = useAdditionalLoginProviders();
    const { getLabel  } = useIntl();
    const { href: resetPasswordRoute  } = useLink({
        route: 'auth.reset-password'
    }) || {};
    const { href: signUpRoute  } = useLink({
        route: 'auth.signup'
    }) || {};
    const [hasFormValidationError, setHasFormValidationError] = React.useState(false);
    const [loginForm, setLoginForm] = React.useState({
        email: swcHelpers.objectSpread({
            name: 'email'
        }, initialFieldState),
        password: swcHelpers.objectSpread({
            name: 'password'
        }, initialFieldState)
    });
    const { initializeSocialLoginFlow  } = useSocialLoginFlow();
    const formattedError = React.useMemo(()=>{
        if (statusFailed(auth.loginCall)) {
            const { code , error_description , message  } = auth.loginCall.error;
            if (code === Auth0ErrorCode.AccessDenied) {
                return getLabel(`${ERROR_PREFIX}.invalidCredentials`);
            }
            if (code === Auth0ErrorCode.PasswordLeaked) {
                return getLabel(`${ERROR_PREFIX}.passwordLeaked`);
            }
            return error_description || message || '';
        }
        if (error === UrlStateParamError.RequiresPasswordChange) {
            return;
        }
        // If this is true, it is one of the other errors from the URL state parameter
        if (typeof error === 'string') {
            return getLabel(`${ERROR_PREFIX}.${error}`);
        }
        if (hasFormValidationError) {
            return getLabel(`${ERROR_PREFIX}.formValidation`);
        }
    }, [
        error,
        auth.loginCall,
        getLabel,
        hasFormValidationError
    ]);
    const handleSubmit = React.useCallback((state)=>{
        var ref, ref1, ref2, ref3;
        const { formState  } = state;
        if ((formState === null || formState === void 0 ? void 0 : (ref = formState.email) === null || ref === void 0 ? void 0 : ref.isValid) && (formState === null || formState === void 0 ? void 0 : (ref1 = formState.password) === null || ref1 === void 0 ? void 0 : ref1.isValid)) {
            setHasFormValidationError(false);
            loginWithPassword1({
                email: formState.email.value,
                password: formState.password.value,
                returnPath
            });
        }
        if (!(formState === null || formState === void 0 ? void 0 : (ref2 = formState.email) === null || ref2 === void 0 ? void 0 : ref2.isTouched) || !(formState === null || formState === void 0 ? void 0 : (ref3 = formState.password) === null || ref3 === void 0 ? void 0 : ref3.isTouched)) {
            return setLoginForm({
                email: swcHelpers.objectSpread({}, formState.email, {
                    isTouched: true
                }),
                password: swcHelpers.objectSpread({}, formState.password, {
                    isTouched: true
                })
            });
        }
        setHasFormValidationError(true);
    }, [
        loginWithPassword1,
        returnPath,
        hasFormValidationError,
        setHasFormValidationError,
        setLoginForm
    ]);
    const wrapperClasses = classnames('login', className);
    const isLoading = statusLoading(auth.loginCall);
    const { commonEventProperties  } = useTealium();
    const loginPageView = React.useCallback(()=>Promise.resolve(swcHelpers.objectSpread({
            id: 'VirtualPage_View',
            Page_Section1: 'Account',
            Page_Type: 'Login&Register',
            Page_Design: 'Login'
        }, commonEventProperties))
    , [
        commonEventProperties
    ]);
    useTrackPageView(loginPageView);
    return /*#__PURE__*/ React.createElement("div", {
        className: wrapperClasses
    }, /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("section", {
        className: "login__section"
    }, showTitle && /*#__PURE__*/ React.createElement(Heading, {
        variant: hasProviders ? 'small' : 'medium',
        className: "login__section-title"
    }, getLabel(`${LABEL_PREFIX}.title`)), showSocialLogin && hasProviders && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SocialLogin, {
        onLogin: initializeSocialLoginFlow,
        returnPath: returnPath,
        labels: {
            googleButton: getLabel(`${LABEL_PREFIX}.social.google`),
            consent: getLabel(`${LABEL_PREFIX}.social.consent`)
        }
    }), /*#__PURE__*/ React.createElement(Divider, {
        className: "login__divider",
        caption: getLabel(`account.signup.general.or`)
    }), /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        className: "login__section-title"
    }, getLabel(`${LABEL_PREFIX}.title.emailLogin`))), !isLoading && formattedError && /*#__PURE__*/ React.createElement(ScrollIntoView, {
        key: formattedError
    }, /*#__PURE__*/ React.createElement(Alert, {
        type: "error",
        icon: "info",
        message: formattedError
    })), error === UrlStateParamError.RequiresPasswordChange && /*#__PURE__*/ React.createElement(ScrollIntoView, {
        key: error
    }, /*#__PURE__*/ React.createElement(Alert, {
        type: "warning",
        icon: "info",
        className: "login__non-compliancy-alert"
    }, /*#__PURE__*/ React.createElement(NonCompliantPasswordMessage, {
        resetPassword: resetPassword1,
        email: email
    }))), /*#__PURE__*/ React.createElement(Form, {
        onSubmit: handleSubmit,
        initialFormState: loginForm,
        name: "login",
        "data-t": "login-form",
        showErrorsOnInvalidSubmit: false
    }, /*#__PURE__*/ React.createElement(Email, {
        "data-t": "email",
        required: true,
        label: getLabel('fields.input.email.label'),
        errorMessage: getLabel('fields.input.email.errorMessage'),
        hideAsteriskSign: true
    }), /*#__PURE__*/ React.createElement(Password, {
        "data-t": "password",
        required: true,
        label: getLabel('fields.input.password.label'),
        errorMessage: getLabel('fields.input.password.errorMessage'),
        hideAsteriskSign: true
    }), /*#__PURE__*/ React.createElement(Link, {
        className: "login__forgot-password",
        label: getLabel('account.action.resetPassword'),
        url: resetPasswordRoute || '#'
    }), /*#__PURE__*/ React.createElement(Button, {
        "data-t": "submit",
        type: "submit",
        isLoading: isLoading,
        caption: getLabel('account.action.login'),
        className: "login__button"
    }))), !disableSignup && /*#__PURE__*/ React.createElement("section", {
        className: "login__section"
    }, createAccountRichText && /*#__PURE__*/ React.createElement(RichTextArea, {
        content: createAccountRichText
    }), /*#__PURE__*/ React.createElement(Button, {
        "data-t": "create-account",
        variant: "outline",
        caption: getLabel('account.action.createAccount'),
        className: "login__button login__button--signup",
        href: signUpRoute || '#'
    }))));
};
const mapStateToProps = (state)=>({
        auth: getAuth(state),
        routes: getFormattedRoutes(state)
    })
;
const mapDispatchToProps = {
    loginWithPassword,
    resetPassword
};
var Login$1 = connect(mapStateToProps, mapDispatchToProps)(Login);

export { Auth0ErrorCode, ERROR_PREFIX, LABEL_PREFIX, Login, NonCompliantPasswordMessage, UrlStateParamError, Login$1 as default, mapDispatchToProps, mapStateToProps };
//# sourceMappingURL=Login.js.map
